<template>
	<app-page>
		<app-tabs app :tabs="tabs">
			<template v-slot:tab-item="{ tab }">
				<app-tab-item :tab="tab" value="assets">
					<app-heading :title="`${website.name} External Assets`" :breadcrumb-append="ancestors">
						<template v-slot:left>
							<app-button v-if="$ravello.can('website.post')"
								class="d-none d-sm-inline-flex"
								color="success"
								outlined
								small
								:to="{ name: 'website-asset', params: { asset: 'new' }, query: { parent: directory } }"
							>Add Asset</app-button>
						</template>
						<template v-slot:right>
							<app-button v-if="$ravello.can('website.post')"
								class="d-none d-sm-inline-flex"
								color="success"
								outlined
								small
								icon="fa-folder-plus"
								:to="{ name: 'website-asset', params: { asset: 'new' }, query: { directory: 1, parent: directory } }"
							>Create Folder</app-button>
						</template>
					</app-heading>
					<app-row>
						<app-cell>
							<app-data-table
								:items="assets"
								:headers="headers"
								:total="count"
								:refresh.sync="refresh"
								@update="fetchAssets"
							>
								<template v-slot:filename="{ item }">
									<app-icon :icon="item.directory ? 'fa-folder' : 'fa-file'" />
									<span class="ml-4">
										<a v-if="item.directory" @click="openFolder(item._id)">{{ item.filename }}</a>
										<a v-else :href="item.url" target="_blank">{{ item.filename }}</a>
									</span>
								</template>
								<template v-slot:type="{ item }">
									{{ item.directory ? 'Folder' : item.url.split('.').pop() }}
								</template>
								<template v-slot:actions="{ item }">
									<app-button
										table
										v-if="!item.directory"
										color="secondary"
										icon="far fa-copy"
										@click="onUrlClick(item._id)"
									>Copy URL</app-button>
									<app-button
										v-if="item.directory"
										color="secondary"
										icon="fa-folder-open"
										table
										@click="openFolder(item._id)"
									>
										Open
									</app-button>
									<app-button
										v-else
										color="secondary"
										icon="fa-external-link-alt"
										table
										:href="item.url"
										target="_blank"
									>
										Open
									</app-button>
									<app-button
										v-if="$ravello.can('website.put')"
										color="secondary"
										:to="{
											name: 'website-asset',
											params: { asset: item._id },
											query: { directory: item.directory, parent: directory }
										}"
										icon="fa-pencil-alt"
										table
									>
										Edit
									</app-button>
									<app-button-confirm
										v-if="$ravello.can('website.delete')"
										color="error"
										icon="fa-trash"
										table
										label-confirm="Remove"
										:description="`Are you sure you want to remove asset <b>${item.filename}</b>?`"
										title="Remove Asset"
										@click="onDelete(item._id)"
									>
										Remove
									</app-button-confirm>
								</template>
							</app-data-table>
						</app-cell>
					</app-row>
					<app-floating-button
						class="d-inline-flex d-sm-none"
						:to="{ name: 'website-asset', params: { asset: 'new' }, query: { directory: 1, parent: directory } }"
						color="success"
						icon="fa-plus"
					/>
				</app-tab-item>
			</template>
		</app-tabs>
	</app-page>
</template>

<script>
import { websiteContentTabs } from '@/datasources/tabs'

export default {
	data() {
		return {
			website: {},
			assets: [],
			count: -1,
			refresh: false,
			tabs: [],
			currentDirectory: null,
			headers: [{
				text: 'Name',
				value: 'filename',
				sortable: true,
			}, {
				text: 'Type',
				value: 'type',
				sortable: false,
			}, {
				text: 'Actions',
				value: 'actions',
				align: 'end',
				sortable: false,
			}],
		}
	},
	computed: {
		websiteId() {
			return this.$route.params.website
		},
		directory() {
			return this.$route.query.directory
		},
		ancestors() {
			if (!this.currentDirectory) { return [] }
			return [...((this.currentDirectory.ancestors || []).map(({ filename, node }) => ({
				text: filename,
				to: { query: { directory: node } },
			}))), {
				text: this.currentDirectory.filename,
				to: { query: { directory: this.currentDirectory._id } },
			}]
		},
	},
	watch: {
		directory() {
			this.refresh = 'hard'
			this.loadFolder()
		},
	},
	async mounted() {
		this.website = await this.$ravello.graphql('website', this.websiteId)
			.fields(['name', 'slug'])
			.query()

		this.tabs = websiteContentTabs(this.websiteId)
		this.loadFolder()
	},
	methods: {
		async onUrlClick(id) {
			try {
				await this.$copyText(`${this.$ravello.constructor.baseUrl}v2/websites/${this.website.slug}/public/assets/${id}`)
				this.$notifications.pushSnackbar('Asset url copied to clipboard!')
			} catch (err) {
				this.$notifications.pushAlert('error', err.message)
			}
		},

		openFolder(id) {
			this.$router.push({ query: { directory: id } })
		},
		async loadFolder() {
			if (!this.directory) {
				this.currentDirectory = null
			} else {
				try {
					this.currentDirectory = await this.$ravello.graphql('asset', this.directory)
						.parent(this.$ravello.graphql('website', this.websiteId))
						.fields(['_id', 'filename', 'ancestors'])
						.query()
				} catch (err) {
					this.$notifications.pushAlert('error', err.data.message)
				}
			}
		},

		async fetchAssets(pagination) {
			const request = this.$ravello.graphql('assets')
				.parent(this.$ravello.graphql('website', this.websiteId))
				.fields(['_id', 'url', 'filename', 'directory', 'parent'])
				.filter('parent', this.directory ? this.directory : { exists: false })
				.fromPagination(pagination)
			if (this.count === -1) {
				request.count('count')
			}
			try {
				this.assets = await request.query()
				if (this.count === -1) {
					this.count = request.results.count
				}
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
		},

		async onDelete(id) {
			this.$store.dispatch('app/startLoading')
			try {
				await this.$ravello.request()
					.v(2)
					.to('websites')
					.to(this.websiteId)
					.to('assets')
					.to(id)
					.delete()
				this.refresh = true
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
			}
			this.$store.dispatch('app/stopLoading')
		},
	},
}
</script>
